/**
 * Card themes (asked for 2026-10-17: "Theme (generic, Birthday, Autumn, Christmas, ...)"):
 * chosen when a card is made, a theme sets the card's paper, writes a first message in
 * the message frame in a fitting hand, and puts a picture from the picture library behind
 * it - so the message frame is never left empty ("zone 12 ... filled by default with a
 * picture"). Nothing is kept of the theme itself: everything it set can be changed in the
 * card like anything else.
 */
import type { FontKey, PaperStyle } from '@foldmycard/scene';
import type { StringKey } from './strings.ts';

export type ThemeId = 'generic' | 'birthday' | 'autumn' | 'christmas' | 'love' | 'thanks' | 'congrats' | 'baby' | 'spring' | 'graduation';

export interface CardTheme {
  id: ThemeId;
  /** The paper; absent is plain white. */
  paper?: PaperStyle;
  /** The first message's look: the words are the theme's own (strings: themeMsg_<id>). */
  note: { font: FontKey; color: string };
  /** A picture of the library to put behind the message: by name, else the first of the group. */
  library: { name: string; group: string };
}

export const THEMES: CardTheme[] = [
  { id: 'generic', note: { font: 'serif', color: '#1b2430' }, library: { name: 'Flowers', group: 'Love & wedding' } },
  { id: 'birthday', paper: { pattern: 'confetti', color: '#fff7e6', ink: '#d64550' },
    note: { font: 'hand', color: '#b3263a' }, library: { name: 'Balloons', group: 'Birthday' } },
  { id: 'autumn', paper: { pattern: 'leaves', color: '#f6e7d2', ink: '#c8642a' },
    note: { font: 'hand', color: '#8a3b12' }, library: { name: 'Forest', group: 'Nature' } },
  { id: 'christmas', paper: { pattern: 'snow', color: '#14432d', ink: '#ffffff' },
    note: { font: 'hand', color: '#a3141f' }, library: { name: 'Christmas tree', group: 'Winter & holidays' } },
  { id: 'love', paper: { pattern: 'hearts', color: '#fde4ec', ink: '#e76fa6' },
    note: { font: 'hand', color: '#b0174f' }, library: { name: 'Hearts', group: 'Love & wedding' } },
  { id: 'thanks', paper: { pattern: 'dots', color: '#e3f1fb', ink: '#a9cdee' },
    note: { font: 'serif', color: '#1f4e8c' }, library: { name: 'Flowers', group: 'Love & wedding' } },
  { id: 'congrats', paper: { pattern: 'stars', color: '#efe7fb', ink: '#d4a017' },
    note: { font: 'hand', color: '#5b2a8c' }, library: { name: 'Party hats', group: 'Birthday' } },
  // more (2026-10-18): the new patterns, with the library's drawings for each occasion
  { id: 'baby', paper: { pattern: 'bubbles', color: '#e3f1fb', ink: '#3f7fd9' },
    note: { font: 'hand', color: '#1f4e8c' }, library: { name: 'New baby', group: 'Milestones' } },
  { id: 'spring', paper: { pattern: 'flowers', color: '#e5f6ec', ink: '#e76fa6' },
    note: { font: 'hand', color: '#2e7d4f' }, library: { name: 'Flowers', group: 'Love & wedding' } },
  { id: 'graduation', paper: { pattern: 'stripes', color: '#0d2a4f', ink: '#d4a017' },
    note: { font: 'serif', color: '#1b2430' }, library: { name: 'Graduation', group: 'Milestones' } },
];

export const themeName = (id: ThemeId) => `theme_${id}` as StringKey;
export const themeMessage = (id: ThemeId) => `themeMsg_${id}` as StringKey;
